import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {
  getStyleDataForStyleId,
  hasCompatibleStyles,
  getProductViews
} from '~/selectors/ProductSelectors';
import {getIconsArray} from '~/selectors/CanvasSelectors';
import {getQuantitiesJs} from '~/selectors/QuoteSelectors';
import ComplementCard from './ComplementCard';
import MultiItemComplementCard from './MultiItemComplementCard';
import OrderAnythingCard from '~/components/OrderAnythingCard';
import cl from 'classnames';
import {isMultiItemEligible} from '~/selectors/ConfigSelectors';
import {useColorMatching} from './useColorMatching';
import {useMetrics} from './useMetrics';
import {useProductSelection} from './useProductSelection';
import MultiItemCardSkeleton from './MultiItemCardSkeleton';
import ComplementCardSkeleton from './ComplementCardSkeleton';

import './index.scss';
/**
 * @param {object} props - Component properties.
 * @param {number} [props.count=4] - Number of skeleton cards to render.
 * @param {React.Component} props.skeletonCard - The skeleton card component to render.
 */
function LoadingState({count = 4, skeletonCard}) {
  const SkeletonCard = skeletonCard;
  return Array(count)
    .fill(0)
    .map((_, index) => <SkeletonCard aria-busy key={index} />);
}

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export default function ColorMatchedComplements({
  selectedProduct,
  addItemsPath = false,
  addStylesMetrics = () => {},
  anchorStyle,
  baseStyle = () => {},
  colorId,
  complementStyles = [],
  detailed,
  fireSkirtTag = () => {},
  inSkirt,
  isAlternatives,
  isWide = false,
  limitComplementCount = false,
  quoteFormPath = false,
  selectedProducts,
  selectedProductType,
  showAnchorStyle = true,
  showOrderMore = true,
  styleId,
  onComplementClick
}) {
  const compatibleStyles = useSelector(hasCompatibleStyles);
  const productViews = useSelector(getProductViews);
  const icons = useSelector(getIconsArray);
  const multiItemEligible = useSelector(isMultiItemEligible);
  const quantities = useSelector(getQuantitiesJs);
  const styleData = useSelector(getStyleDataForStyleId);

  const {
    colorMatches,
    isLoading,
    isError,
    filteredAlternatives,
    getColorId,
    shouldSkip
  } = useColorMatching({
    anchorStyle,
    selectedProduct,
    complementStyles,
    isAlternatives,
    colorId,
    quantities
  });

  const {fireStyleMetrics, fireOrderAnythingMetrics} = useMetrics({
    addItemsPath,
    quoteFormPath,
    inSkirt,
    fireSkirtTag
  });

  const {handleComplementSelected, handleAlternativeSelected} =
    useProductSelection(fireStyleMetrics, selectedProduct);

  const complementStylesCollection = useMemo(
    () =>
      isAlternatives && filteredAlternatives?.length > 0
        ? filteredAlternatives.slice(0, 4)
        : limitComplementCount
          ? complementStyles.slice(0, 3)
          : complementStyles,
    [
      isAlternatives,
      filteredAlternatives,
      limitComplementCount,
      complementStyles
    ]
  );

  if (shouldSkip) return null;
  if (isError) return null;

  const complementsClassName = cl('ndx-ColorMatchedComplements', {
    'ndx-ColorMatchedComplements--wide': isWide,
    isMultiItem: multiItemEligible && quoteFormPath,
    isAlternatives
  });

  const getDetailTitle = style =>
    style.id === selectedProduct.styleId
      ? 'Same item, different color'
      : selectedProductType === style.product_type
        ? 'Recommended'
        : style.product_type;

  const currentStyle = styleData(styleId);
  const baseStyleData = baseStyle(styleId);
  const anchorColorId =
    anchorStyle &&
    String(
      colorMatches?.[anchorStyle.id] ||
        (anchorStyle.colors.find(c => c.status === 'active') || {}).id
    );

  const shouldShowAnchorStyle =
    (multiItemEligible && !quoteFormPath && anchorStyle && showAnchorStyle) ||
    (!multiItemEligible && anchorStyle && showAnchorStyle);

  const shouldShowBaseStyle = isAlternatives && !quoteFormPath && baseStyleData;

  const shouldShowOrderMore =
    showOrderMore &&
    compatibleStyles &&
    (!multiItemEligible || (multiItemEligible && !quoteFormPath));

  const shouldShowMultiItemCard = multiItemEligible && quoteFormPath;

  if (isLoading) {
    return (
      <div className={complementsClassName}>
        <LoadingState
          aria-busy
          count={
            isAlternatives || shouldShowMultiItemCard
              ? 4
              : complementStyles.length
          }
          skeletonCard={
            shouldShowMultiItemCard
              ? MultiItemCardSkeleton
              : ComplementCardSkeleton
          }
        />
      </div>
    );
  }

  return (
    <div className={complementsClassName}>
      {shouldShowAnchorStyle && (
        <ComplementCard
          key={anchorStyle.id}
          colorId={anchorColorId}
          detailed={detailed}
          fireSkirtTag={fireSkirtTag}
          anchorStyle={anchorStyle}
          getProductViews={productViews}
          icons={icons}
          inSkirt={inSkirt}
          isAlternatives={isAlternatives}
          selected={anchorStyle.id === selectedProduct.styleId}
          style={anchorStyle}
          onClick={() =>
            handleComplementSelected(
              anchorStyle,
              anchorColorId,
              onComplementClick
            )
          }
        />
      )}

      {shouldShowBaseStyle && (
        <ComplementCard
          key={styleId}
          colorId={colorId}
          detailed={detailed}
          fireSkirtTag={fireSkirtTag}
          getProductViews={productViews}
          icons={icons}
          inSkirt={inSkirt}
          isAlternatives={isAlternatives}
          selected={false}
          style={baseStyleData}
          anchorStyle={baseStyleData}
          baseStyle={baseStyleData}
        />
      )}

      {isAlternatives &&
        complementStylesCollection.map(({alternativeId, style}, index) => {
          const colorId = getColorId(alternativeId);
          if (!colorId) return null;

          return (
            ((multiItemEligible && !quoteFormPath) || !multiItemEligible) && (
              <ComplementCard
                key={style.id}
                colorId={colorId}
                detailed={detailed}
                fireSkirtTag={fireSkirtTag}
                anchorStyle={anchorStyle}
                baseStyle={baseStyleData}
                getProductViews={productViews}
                icons={icons}
                inSkirt={inSkirt}
                selected={style.id === selectedProduct.styleId}
                style={style}
                onClick={() =>
                  handleAlternativeSelected(style, colorId, styleId)
                }
                isAlternatives={isAlternatives}
                index={index}
              />
            )
          );
        })}

      {!isAlternatives &&
        complementStylesCollection.map(({complementId, style}) => {
          const colorId = getColorId(complementId);
          return (
            ((multiItemEligible && !quoteFormPath) || !multiItemEligible) &&
            style &&
            colorId && (
              <ComplementCard
                key={style.id}
                colorId={String(colorId)}
                detailed={detailed}
                fireSkirtTag={fireSkirtTag}
                anchorStyle={anchorStyle}
                getProductViews={productViews}
                icons={icons}
                inSkirt={inSkirt}
                selected={style.id === selectedProduct.styleId}
                style={style}
                onClick={() =>
                  handleComplementSelected(style, colorId, onComplementClick)
                }
                isAlternatives={isAlternatives}
              />
            )
          );
        })}

      {complementStylesCollection.map(({complementId, style}) => {
        const colorId = getColorId(complementId);
        if (!colorId) return null;

        return (
          shouldShowMultiItemCard &&
          style && (
            <MultiItemComplementCard
              key={style.id}
              colorId={colorId}
              detailed={detailed}
              fireSkirtTag={fireSkirtTag}
              anchorStyle={anchorStyle}
              getProductViews={productViews}
              icons={icons}
              inSkirt={inSkirt}
              selected={style.id === selectedProduct.styleId}
              selectedProduct={selectedProduct.styleId}
              selectedProducts={selectedProducts}
              style={style}
              onClick={() =>
                handleComplementSelected(style, colorId, onComplementClick)
              }
              title={getDetailTitle(style)}
            />
          )
        );
      })}

      {shouldShowMultiItemCard && anchorStyle && showAnchorStyle && (
        <MultiItemComplementCard
          key={anchorStyle.id}
          colorId={anchorColorId}
          detailed={detailed}
          fireSkirtTag={fireSkirtTag}
          anchorStyle={anchorStyle}
          getProductViews={productViews}
          title={getDetailTitle(anchorStyle)}
          colors={anchorStyle.colors.length}
          icons={icons}
          inSkirt={inSkirt}
          selected={anchorStyle.id === selectedProduct.styleId}
          selectedProducts={selectedProducts}
          style={anchorStyle}
          onClick={() =>
            handleComplementSelected(
              anchorStyle,
              anchorColorId,
              onComplementClick
            )
          }
        />
      )}

      {shouldShowOrderMore && (
        <OrderAnythingCard
          addItemsPath={addItemsPath}
          addStylesMetrics={addStylesMetrics}
          anchorStyle={anchorStyle}
          fireOrderAnythingMetrics={fireOrderAnythingMetrics}
          hasComplements={complementStyles.length > 0}
          quoteFormPath={quoteFormPath}
          isAlternatives={isAlternatives}
          currentStyleCategory={currentStyle?.primary_category_name}
          currentStyleCategoryId={currentStyle?.primary_category_id}
        />
      )}
    </div>
  );
}

const propTypes = {
  selectedProduct: PropTypes.object.isRequired,
  addItemsPath: PropTypes.bool,
  addStylesMetrics: PropTypes.func,
  anchorStyle: PropTypes.object,
  baseStyle: PropTypes.func,
  colorId: PropTypes.string,
  complementStyles: PropTypes.array,
  detailed: PropTypes.bool,
  fireSkirtTag: PropTypes.func,
  inSkirt: PropTypes.bool,
  isAlternatives: PropTypes.bool,
  isWide: PropTypes.bool,
  limitComplementCount: PropTypes.bool,
  quoteFormPath: PropTypes.bool,
  selectedProducts: PropTypes.array,
  selectedProductType: PropTypes.string,
  showAnchorStyle: PropTypes.bool,
  showOrderMore: PropTypes.bool,
  styleId: PropTypes.string,
  onComplementClick: PropTypes.func
};

ColorMatchedComplements.propTypes = propTypes;
